<template>
  <div class="meals-info-card">
    <div class="header">
      <slot> Event Meal Title </slot>
    </div>
    <div class="content">
      <div v-if="isMenuDisplayed" class="menu-container">
        <div class="no-meal-label">
          <input
            :id="eventRadioGroup.id"
            :name="eventRadioGroup.name"
            :checked="noMeals"
            :disabled="isRadioDisabled"
            :accesskey="accessKeys[mealEvent.name].none"
            type="radio"
            @click="removeOrdersByMenuEvent($event)"
          />
          <label :for="eventRadioGroup.id" :class="{ 'opaque-class': shouldDisplayOpaque }">
            {{ $t(mealEvent.noMealLabel) }}
          </label>
        </div>
        <div v-for="meal in meals" :key="meal.radioId">
          <div class="meal-label">
            <input
              :id="meal.radioId"
              :name="eventRadioGroup.name"
              :checked="meal.checked"
              :disabled="isRadioDisabled"
              :accesskey="accessKeys[mealEvent.name][meal.diet.toLowerCase()]"
              type="radio"
              @click="addOrder(meal, $event)"
            />
            <label :for="meal.radioId" :class="{ 'opaque-class': shouldDisplayOpaque }">
              {{ meal.name }}
              <svg-icon :icon="meal.diet.toLowerCase()" class="svg-diet-icon" />
            </label>
          </div>
        </div>
        <div v-if="isHoliday">
          {{ $t('calendar.holidayMeals') }}
        </div>
        <div v-if="noDietaryOptions">
          {{ $t('calendar.noDiets') }}
        </div>
        <div v-if="extras.length && meals.length" class="extras">
          <label class="extras-title-label">Extras</label>
          <div v-for="extra in extras" :key="extra.radioId">
            <div>
              <label :for="extra.radioId" :class="{ 'opaque-class': shouldDisplayOpaque }">
                {{ extra.name }} ({{ $t('settings.checkboxes.includedLabel') }})
              </label>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="no-menu-container">
        <svg-icon v-if="mealEvent.hasSVGIcon" :icon="mealEvent.noMenuIcon" class="no-menu-icon" />
        <div v-else class="no-menu-text">
          {{ $t(mealEvent.noMenuText) }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { sentence } from 'case';
import SvgIcon from '@/components/SvgIcon';
import { EXTRAS_NAME, EXTRAS_ID, NAVIGATION_ACCESS_KEYS } from '@/constants';
import { mapState, mapActions } from 'vuex';
import holidayFilter from '@/utils/holidayFilter';

export default {
  name: 'MealsCard',
  components: {
    SvgIcon,
  },
  data() {
    return {
      accessKeys: NAVIGATION_ACCESS_KEYS.menu,
    };
  },
  props: {
    mealsInfo: {
      type: Array,
      required: true,
    },
    dayName: {
      type: String,
      required: true,
    },
    mealEvent: {
      type: Object,
      required: true,
    },
    canUserEdit: {
      type: Boolean,
      required: true,
    },
    menuEventId: {
      type: Number,
      required: true,
    },
  },
  computed: {
    ...mapState('weeklyMenu', {
      disabledDays: (state) => state.disabledDays,
      disabledEvents: (state) => state.disabledEvents,
    }),
    ...mapState('orders', { orders: (state) => state.byUserWeek }),
    ...mapState('user', {
      userOffice: (state) => state.user.office,
      userDiets: (state) => state.user.dietsInfo,
    }),

    isMenuDisplayed() {
      return this.mealsInfo.length !== 0;
    },

    eventRadioGroup() {
      return {
        name: `${this.dayName}.${this.mealEvent.name}`,
        id: `${this.dayName}.${this.mealEvent.name}.NoMeal`,
      };
    },

    mealsFromDietaryOptions() {
      return this.splitMealsByIsExtra(false).map((meal) => ({
        ...meal,
        name: sentence(meal.name),
      }));
    },

    meals() {
      // filter out holidays and "-" meals
      return this.mealsFromDietaryOptions.filter((meal) => holidayFilter(meal.name));
    },

    isHoliday() {
      if (this.noDietaryOptions) {
        return false;
      }

      // if there were dietary options, but no meals, they were all holiday meals
      return this.meals.length === 0 && this.mealsInfo.length > 0;
    },

    noDietaryOptions() {
      return this.mealsFromDietaryOptions.length === 0;
    },

    noMeals() {
      return !this.splitMealsByIsExtra(false).reduce((acc, meal) => acc || meal.checked, false);
    },

    extras() {
      return this.splitMealsByIsExtra(true)
        .map((extra) => ({
          ...extra,
          name: sentence(extra.name),
        }))
        .filter((extra) => extra.name !== '-');
    },

    shouldDisplayOpaque() {
      return this.disabledDays[this.dayName] || this.disabledEvents[this.mealEvent.name];
    },

    isRadioDisabled() {
      return this.shouldDisplayOpaque || !this.canUserEdit;
    },

    containsExtras() {
      return this.orders.some(
        (order) =>
          order.menuEvent === this.menuEventId && order.mealInfo.dietInfo.name === EXTRAS_NAME
      );
    },
  },
  methods: {
    ...mapActions('orders', {
      insertOrder: 'insertOrder',
      removeOrderByMenuEvent: 'removeOrderByMenuEvent',
      removeOrderById: 'removeOrderById',
    }),
    addOrder(meal) {
      if (!this.canUserEdit) {
        return;
      }
      this.insertOrder({
        menuEvent: this.menuEventId,
        meal: meal.mealId,
        office: this.userOffice,
      });
    },
    removeOrdersByMenuEvent() {
      const mealOrders = this.orders.filter((order) => order.menuEvent === this.menuEventId);
      if (mealOrders.length === 0 || !this.canUserEdit) {
        return;
      }
      mealOrders.forEach((mealOrder) => {
        this.removeOrderByMenuEvent({
          menuEvent: this.menuEventId,
          meal: mealOrder.meal,
        });
      });
    },
    removeOrder(meal) {
      if (!this.canUserEdit) {
        return;
      }
      this.removeOrderById(meal.orderId);
    },
    updateOrder(meal) {
      if (!this.containsExtras) {
        this.addOrder(meal);
      } else {
        this.removeOrder(meal);
      }
    },
    splitMealsByIsExtra(isExtraExpected) {
      return this.mealsInfo.reduce((accumulator, meal) => {
        const diet = meal.dietInfo.name;

        const isExtra = meal.dietInfo.id === EXTRAS_ID;
        const userHasDiet = this.userDiets.includes(diet);

        if (isExtra === isExtraExpected && (userHasDiet || !this.canUserEdit)) {
          const userOrder = this.orders.find(
            (order) =>
              order.meal === meal.id &&
              order.menuEvent === this.menuEventId &&
              !this.shouldDisplayOpaque
          );

          accumulator.push({
            radioId: `${this.dayName}.${this.mealEvent.id}_${meal.id}`,
            diet,
            name: meal.name,
            mealId: meal.id,
            checked: Boolean(userOrder),
            orderId: userOrder ? userOrder.id : null,
          });
        }
        return accumulator;
      }, []);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/variables';

$contentMinHeight: 468px;

/deep/ .el-checkbox {
  &__inner {
    z-index: unset;
  }
}
.meals-info-card {
  flex: 1;

  .header {
    background-color: $header-background-color;
    text-align: center;
    color: $card-header-text-color;
    padding: 0;
    height: 40px;
    line-height: 40px;
    font-size: 20px;
    font-weight: 600;
  }

  .content {
    padding: 20px;
    font-size: $label-text-size;
    min-height: $contentMinHeight;

    .no-meal-label {
      padding-top: 10px;
      margin-bottom: 30px;

      label {
        margin-left: 10px;
      }
    }

    .meal-label {
      margin-bottom: 30px;
      label {
        display: block;
        margin-left: 25px;
        word-break: normal;
      }
      input {
        margin-top: 5px;
        float: left;
      }
      .el-checkbox {
        margin-left: 0;
        float: left;
      }
    }

    .extras {
      margin-top: auto;
      display: flex;
      flex-direction: column;
      .extras-title-label {
        font-size: $card-title;
        font-weight: $bold-font;
        margin-bottom: 10px;
      }
    }

    .no-menu-icon {
      width: 66px;
      height: 66px;
    }

    .no-menu-text {
      font-size: $subtitle-font-size;
      font-weight: 600;
      text-align: center;
      color: $dark-grey-color;
      word-break: normal;
    }

    .opaque-class {
      opacity: $opacity-disabled-sections;
    }
  }

  @media only screen and (max-width: 768px) {
    .content {
      min-height: $contentMinHeight / 3;
    }
  }

  .no-menu-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .menu-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
}

.meals-info-card:not(:last-child) {
  border-right: $table-border-color;
}
</style>



// WEBPACK FOOTER //
// src/components/weeklymenu/MealsCard.vue